// Typed wrapper over the broker product catalog (PMS-S141, BRK-S051):
// GET /api/broker/products returns the ACTIVE products with their full in-code
// manifests — the canonical field catalog, the indication/quote floors (derived
// server-side from the registered carriers' declared required fields), and the
// offer-terms display schema the comparison table renders. This replaces the
// hardcoded client product list (coverageLines.ts). Types are derived from the
// PMS contract (docs/openapi.yaml → brokerProductSchema), like the sibling
// seams (brokerAccounts.ts / brokerSubmissions.ts).

import type { AuthedRequester } from "@/lib/brokerAuth";

/** The PMS coverage-line enum — the `line` a submission is created with. */
export type LineOfBusiness =
  | "general_liability"
  | "workers_compensation"
  | "commercial_auto";

/** The registered product-manifest keys (PMS lib/products registry). */
export type ProductKey =
  | "workers_compensation"
  | "contractors_gl"
  | "cannabis_commercial_auto";

/** Where a canonical field lives: `submission` fields go in the submission's
 *  field bag (PMS-S142); `account` fields live on the Account — the manifest
 *  only declares them so quote-readiness can name what's missing. */
export type FieldScope = "account" | "submission";

/** The two quoting stages a floor is derived for: a rough indication vs a firm,
 *  bindable quote (which typically needs more of the insured profile). */
export type QuoteStage = "indication" | "quote";

/** Manifest field control types. `classCode` renders the class-code typeahead;
 *  `group` is a repeatable row set whose shape is the `item` array. */
export type FieldType =
  | "text"
  | "number"
  | "currency"
  | "date"
  | "select"
  | "boolean"
  | "classCode"
  | "group";

export interface FieldOption {
  value: string;
  label: string;
}

export type FieldDefault = string | number | boolean | string[];

/** A sub-field of a repeatable group row (no scope — it lives inside the
 *  group's rows). `optional` marks row fields the broker may leave blank. */
export interface GroupItemField {
  key: string;
  label: string;
  type: FieldType;
  helpText?: string;
  options?: FieldOption[];
  multiple?: boolean;
  defaultValue?: FieldDefault;
  optional?: boolean;
}

/** One canonical field in a product's catalog. Published flat with optional
 *  facets (options/multiple for selects, item/minItems for groups) — the shape
 *  of the PMS fieldDefSchema. */
export interface ManifestField {
  key: string;
  label: string;
  scope: FieldScope;
  type: FieldType;
  helpText?: string;
  options?: FieldOption[];
  multiple?: boolean;
  defaultValue?: FieldDefault;
  item?: GroupItemField[];
  minItems?: number;
}

/** An ordered group of offer-term rows the comparison table renders; termKeys
 *  reference canonical field keys (labels/formats come from the field defs). */
export interface OfferTermsGroup {
  label: string;
  termKeys: string[];
}

/** A product's full manifest: field catalog + derived minimum-information
 *  floors per quoting stage + offer-terms display schema. */
export interface ProductManifest {
  key: ProductKey;
  label: string;
  lineOfBusiness: LineOfBusiness;
  fields: ManifestField[];
  offerTerms: OfferTermsGroup[];
  floors: {
    indication: string[];
    quote: string[];
  };
}

/** One row of the catalog. `manifest` is null for a product with no registered
 *  manifest (the legacy single-carrier GL). */
export interface BrokerProduct {
  id: string;
  name: string;
  lineOfBusiness: LineOfBusiness;
  distributionMode: "single_carrier" | "marketplace";
  manifest: ProductManifest | null;
}

/** A canonical field reference (key + label + where it lives). Used for the
 *  structured quote-readiness / account-floor checklists (PMS-S144, BRK-S057),
 *  and the shape of the server's `details.missingFields`. Lives here — the
 *  manifest/canonical-field domain — so both the products/account floor logic
 *  and the submissions seam share one type without an import cycle. */
export interface MissingField {
  key: string;
  label: string;
  scope: FieldScope;
}

/** GET /api/broker/products — the active product catalog with manifests. */
export function getProducts(
  request: AuthedRequester,
): Promise<{ items: BrokerProduct[] }> {
  return request<{ items: BrokerProduct[] }>("/api/broker/products");
}

/**
 * The ACCOUNT-scope fields on a given stage's floor (BRK-S057) — the insured
 * Account fields that must be satisfied before that stage. Submission-scope
 * floor fields are captured in the manifest details step, not the account; the
 * account never carries them. Derived from the manifest the server already
 * serves (fields carry `scope`, floors carry stages) — no product conditionals.
 */
export function accountFloorFields(
  manifest: ProductManifest,
  stage: QuoteStage,
): ManifestField[] {
  const keys = new Set(manifest.floors[stage]);
  return manifest.fields.filter(
    (f) => f.scope === "account" && keys.has(f.key),
  );
}

/** Product-picker options for starting a submission: one option per PRODUCT,
 *  valued by its id (the create is product-addressed since PMS-S147 — a line
 *  can carry several products, so a line-keyed option would be ambiguous). */
export function productOptions(products: BrokerProduct[]): FieldOption[] {
  return products.map((p) => ({ value: p.id, label: p.name }));
}
